import { kDownloadFileExtensions, kPageMetaType } from '../constants'

type StrapiPageLink = {
  label?: string | null
  slug?: string | null
  /** Url including parent page slugs, resolved in src/schema/resolvers.ts */
  path?: string | null
  url?: string | null
  metaType?: string | null
  titleInternal?: string | null
  identifier?: string | null
  title?: string | null
}

/**
 * Editorial urls migrated from Contentful may point at a static asset with a
 * document relative path (e.g. `../media/pdf/foo.pdf`). Those resolve against
 * the current route, so they only work on first level pages. Assets are served
 * from the site root, so make the path root relative.
 */
function normalizeExternalUrl(url: string): string {
  if (/^([a-z][a-z0-9+.-]*:|\/\/|\/|#|mailto:|tel:)/i.test(url)) {
    return url
  }

  return `/${url.replace(/^(\.\.?\/)+/, '')}`
}

function isDownloadUrl(url: string): boolean {
  const extension = url.split(/[?#]/)[0].split('.').pop()?.toLowerCase()

  return !!extension && kDownloadFileExtensions.includes(extension)
}

/**
 * Editorial slugs are not consistently normalized (e.g. `/datenschutz-bewerber`
 * instead of `datenschutz-bewerber`). A leading slash would turn the url into
 * `//datenschutz-bewerber/`, which browsers resolve as a protocol relative url
 * pointing at a foreign host instead of an internal route.
 */
export function normalizePageSlug(slug?: string | null): string {
  return (slug || '').replace(/^\/+/, '').replace(/\/+$/, '')
}

export function pageSlugToUrl(slug?: string | null): string {
  const segment = normalizePageSlug(slug)

  if (!segment) {
    return '/'
  }

  return `/${segment}/`
}

export function strapiPageToLinkTo(
  page?: StrapiPageLink | null,
  meta?: { totalJobcount?: number }
): MCDC.Props.ILinkTo {
  if (!page) {
    return { label: '', url: '/', isExternal: false }
  }

  if (page.url) {
    const url = normalizeExternalUrl(page.url)

    return {
      label: page.label || page.titleInternal || '',
      url,
      isExternal: true,
      isDownload: isDownloadUrl(url),
    }
  }

  if (page.identifier) {
    return {
      label: page.label || page.title || page.titleInternal || '',
      url: `/${page.identifier}/`,
      isExternal: false,
    }
  }

  return {
    label: page.label || page.titleInternal || '',
    url: page.path || pageSlugToUrl(page.slug),
    isExternal: false,
    count:
      page.metaType === kPageMetaType.offers
        ? meta?.totalJobcount || 0
        : undefined,
  }
}
